import * as React from 'react';
import { cn } from '../commons/utils';
import { THEME_V2_CLASS } from '@v2/constants';
import { buttonVariants, renderAffix } from '../commons/button-common';
import type { CommonProps, Icon } from '../types';
import type { ButtonVariant as ButtonCssVariant } from './types';

type ButtonSize = 'small' | 'medium' | 'large';
type ButtonVariant = 'filled' | 'outline' | 'ghost';
type ButtonSkin = 'default' | 'destructive' | 'dark' | 'light';

type BaseProps = {
  /** Visual size of the button
   * @default 'medium'
   */
  size?: ButtonSize;
  /** Button style variant
   * @default 'filled'
   */
  variant?: ButtonVariant;
  /** Color skin of the button
   * @default 'default'
   */
  skin?: ButtonSkin;
  /** Custom width for the button */
  width?: string;
  /** Icon or content rendered after the label */
  suffix?: Icon;
  /** Icon or content rendered before the label */
  prefix?: Icon;
  /** Whether the button uses a fully rounded shape
   * @default false
   */
  round?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
} & CommonProps;

export type ButtonProps<T extends 'button' | 'a' = 'button'> = T extends
  | 'button'
  | 'a'
  ? BaseProps &
      Omit<React.ComponentPropsWithoutRef<T>, keyof BaseProps> & {
        /** HTML element to render as — `button` or anchor (`a`) */
        as?: T;
        /** Ref forwarded to the underlying HTML element */
        ref?: React.Ref<
          T extends 'button' ? HTMLButtonElement : HTMLAnchorElement
        >;
      }
  : never;

const Button = <T extends 'button' | 'a' = 'button'>({
  ref,
  as: Component = 'button',
  variant = 'filled',
  size = 'medium',
  round = false,
  skin = 'default',
  theme,
  width,
  prefix: Prefix,
  suffix: Suffix,
  disabled,
  children,
  className,
  testId,
  ...props
}: ButtonProps<T>) => {
  const cssVariant = `${
    disabled ? 'disabled-' : ''
  }${skin}-${variant}` as const;

  const disabledProp =
    Component === 'button' && disabled !== undefined
      ? { disabled }
      : { ['aria-disabled']: disabled, ...(disabled && { tabIndex: -1 }) };

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    props.onClick?.(e as any);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      return;
    }
    props.onKeyDown?.(e as any);
  };

  return (
    <Component
      {...props}
      data-testid={testId}
      //@ts-ignore - typescript generic cannot see which element is being used
      ref={ref}
      {...disabledProp}
      className={cn(
        THEME_V2_CLASS,
        theme,
        buttonVariants({
          round,
          size,
          variant: cssVariant as ButtonCssVariant,
        }),
        className
      )}
      style={{ width }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {Prefix && renderAffix(Prefix, size)}
      {children}
      {Suffix && renderAffix(Suffix, size)}
    </Component>
  );
};

Button.displayName = 'Button';

export { Button };
